import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import DevicesIcon from "@mui/icons-material/Devices";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import deviceStore from "../../stores/DeviceStore";
import DeviceGrid from "./DeviceGrid";
import DeviceList from "./DeviceList";
import RegisteredDeviceComponent from "./RegisteredDeviceComponent";
import PendingDeviceComponent from "./PendingDeviceComponent";

const DeviceComponent = observer(() => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [viewMode, setViewMode] = useState("grid");
  const [activeTab, setActiveTab] = useState("All Devices");

  useEffect(() => {
    deviceStore.loadDevices();
  }, []);

  const tabList = ["All Devices", "Registered Devices", "Pending Devices"];

  const tabDescriptions = {
    "All Devices": "Manage your devices from this dashboard",
    "Registered Devices": "View and manage all registered devices",
    "Pending Devices": "Review and approve pending devices",
  };

  const showEmptyAll =
    !deviceStore.isLoading && (deviceStore.devices?.length || 0) === 0;

  const error = deviceStore.error;
  const errorMessage = deviceStore.errorMessage;
  const isRegionEmptyError =
    error === 406 &&
    typeof errorMessage === "string" &&
    errorMessage.includes("Empty region list");

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Card */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <DevicesIcon fontSize="large" sx={{ mr: 2, color: "#22C55E" }} />
            <Typography variant="h4" fontWeight="bold">
              {activeTab}
            </Typography>
          </Box>
          <Typography color="text.secondary">
            {tabDescriptions[activeTab]}
          </Typography>
        </CardContent>
      </Card>

      {/* Tab Buttons */}
      <Box className="flex gap-2 mb-4">
        {tabList.map((tab) => (
          <Box
            key={tab}
            sx={{
              borderBottom: "2px solid",
              borderColor:
                activeTab === tab
                  ? theme.palette.mode === "dark"
                    ? theme.palette.primary.main
                    : "#22C55E"
                  : "transparent",
            }}
          >
            <Button
              onClick={() => setActiveTab(tab)}
              sx={{
                textTransform: "none",
                color:
                  activeTab === tab
                    ? theme.palette.mode === "dark"
                      ? theme.palette.primary.main
                      : "#000000"
                    : theme.palette.text.primary,
              }}
            >
              {tab}
            </Button>
          </Box>
        ))}
      </Box>

      {activeTab === "All Devices" && (
        <Box className="hello12 flex justify-between items-center mb-4">
          <Button
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              backgroundColor: "#22C55E",
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#16A34A",
              },
            }}
            onClick={() => navigate("/edit-unit/new")}
          >
            Add Device
          </Button>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(event, newView) => newView && setViewMode(newView)}
          >
            <ToggleButton value="grid">Grid View</ToggleButton>
            <ToggleButton value="table">Table View</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Main Content Area */}
      {activeTab === "Registered Devices" ? (
        <RegisteredDeviceComponent />
      ) : activeTab === "Pending Devices" ? (
        <PendingDeviceComponent />
      ) : showEmptyAll ? (
        <Paper
          elevation={1}
          sx={{
            borderRadius: 3,
            p: 5,
            textAlign: "center",
            bgcolor: "background.paper",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            minHeight: 500,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 90,
              height: 90,
              borderRadius: "50%",
              bgcolor: "primary.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <DevicesOutlinedIcon sx={{ fontSize: 48, color: "primary.main" }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            No Devices Found
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 520, mx: "auto" }}
          >
            {isRegionEmptyError
              ? "You don’t have any regions assigned. Once a region is assigned to your account, all of the devices in that region will appear here."
              : "When devices are registered to your account or region, they will show up here."}
          </Typography>
        </Paper>
      ) : viewMode === "grid" ? (
        <DeviceGrid
          devices={deviceStore.devices}
          loading={deviceStore.isLoading}
        />
      ) : (
        <DeviceList
          devices={deviceStore.devices}
          loading={deviceStore.isLoading}
        />
      )}
    </Box>
  );
});

export default DeviceComponent;
